import { QueryKey } from "@tanstack/react-query";

import { useFetchQuery } from "../core/rest/useFetchQuery";
import {
  BNP_ADMIN_PRIVILEGE,
  REPORTING_MANAGER_PRIVILEGE,
  BNP_DELPHIUS_LEGACY_OG_ADMIN_CLAIM,
  REPORTING_ADMIN_CLAIM,
  BNP_WORKFLOWS_ADMIN_PRIVILEGE,
  BNP_WORKFLOW_APPROVER_PRIVILEGE,
  BNP_FORM_SUBMISSION_PRIVILEGE,
  BNP_MILESTONE_ADMIN_PRIVILEGE,
  BNP_TASK_ASSIGNEE_ONLY_PRIVILEGE,
} from "./constants";
import { QUERY_CACHE_PERIOD } from "../../constants/queryConstants.ts";
import { ServicePath } from "../../constants/apiConstants";

type TClaims = {
  BNP_ADMIN_PRIVILEGE: boolean;
  REPORTING_MANAGER_PRIVILEGE: boolean;
  BNP_DELPHIUS_LEGACY_OG_ADMIN_CLAIM: boolean;
  REPORTING_ADMIN_CLAIM: boolean;
  BNP_WORKFLOWS_ADMIN_PRIVILEGE: boolean;
  BNP_WORKFLOW_APPROVER_PRIVILEGE: boolean;
  BNP_FORM_SUBMISSION_PRIVILEGE: boolean;
  BNP_MILESTONE_ADMIN_PRIVILEGE: boolean;
  BNP_TASK_ASSIGNEE_ONLY_PRIVILEGE: boolean;
};

type TClaim = {
  claimName: keyof TClaims;
  granted: boolean;
};

type TClaimsResponse = {
  result: boolean;
  claims: TClaim[];
};

type TParams = {
  entityUuid?: string;
  userUuid?: string;
};

const getParams = ({ entityUuid, userUuid }: TParams) => {
  const queryKey: QueryKey = ["claims", userUuid, entityUuid];
  const apiEndpoint = `${ServicePath.AccessPolicyV1}/claims/verify`;
  const subject = `subject=urn:opengov:platform:user/${userUuid}`;
  const context = `context=urn:opengov:platform:entity/${entityUuid}`;
  const claims = [
    BNP_ADMIN_PRIVILEGE,
    REPORTING_MANAGER_PRIVILEGE,
    BNP_DELPHIUS_LEGACY_OG_ADMIN_CLAIM,
    REPORTING_ADMIN_CLAIM,
    BNP_WORKFLOWS_ADMIN_PRIVILEGE,
    BNP_WORKFLOW_APPROVER_PRIVILEGE,
    BNP_FORM_SUBMISSION_PRIVILEGE,
    BNP_MILESTONE_ADMIN_PRIVILEGE,
    BNP_TASK_ASSIGNEE_ONLY_PRIVILEGE,
  ];

  const claimsString = claims.join(",");
  const url = `${apiEndpoint}?${subject}&${context}&claim=${claimsString}`;
  return {
    queryKey,
    url,
    options: {
      enabled: !!entityUuid && !!userUuid,
      staleTime: QUERY_CACHE_PERIOD.EXTENDED,
      retry: 3,
      select: (data: TClaimsResponse) =>
        data.claims.reduce<TClaims | Record<string, never>>((acc, { claimName, granted }) => {
          acc[claimName] = granted;
          return acc;
        }, {}),
    },
  };
};

export const useGetClaims = ({ entityUuid, userUuid }: TParams) => useFetchQuery(getParams({ entityUuid, userUuid }));

export const useClaimAccess = ({ entityUuid, userUuid }: TParams) => {
  const { data, isLoading } = useGetClaims({ entityUuid, userUuid });
  return {
    isBnpAdmin: data?.[BNP_ADMIN_PRIVILEGE] || false,
    isReportingManager: data?.[REPORTING_MANAGER_PRIVILEGE] || false,
    isLegacyOgAdmin: data?.[BNP_DELPHIUS_LEGACY_OG_ADMIN_CLAIM] || false,
    isReportingAdmin: data?.[REPORTING_ADMIN_CLAIM] || false,
    isMilestoneAdmin: data?.[BNP_MILESTONE_ADMIN_PRIVILEGE] || false,
    isWorkflowsAdmin: data?.[BNP_WORKFLOWS_ADMIN_PRIVILEGE] || false,
    isWorkflowApprover: data?.[BNP_WORKFLOW_APPROVER_PRIVILEGE] || false,
    isFormSubmitter: data?.[BNP_FORM_SUBMISSION_PRIVILEGE] || false,
    isTaskAssigneeOnly: data?.[BNP_TASK_ASSIGNEE_ONLY_PRIVILEGE] || false,
    isLoading: isLoading || !data,
  };
};
